import { AxiosProgressEvent } from 'axios';

export interface IUploadProgressState {
    progress: number;
    currentFileIndex?: number;
    totalFiles?: number;
}

export type UploadProgressHandler = (state: IUploadProgressState) => void;

const normalizeProgress = (progress: number): number =>
    Math.min(100, Math.max(0, Math.round(progress)));

export const getUploadProgress = (event: AxiosProgressEvent): number | undefined => {
    if (typeof event.progress === 'number') {
        return normalizeProgress(event.progress * 100);
    }

    if (typeof event.total === 'number' && event.total > 0) {
        return normalizeProgress((event.loaded / event.total) * 100);
    }

    return undefined;
};

const getCurrentFileIndex = (loadedBytes: number, files?: File[]): number | undefined => {
    if (!files?.length) {
        return undefined;
    }

    const adjustedLoadedBytes = Math.max(0, loadedBytes - 1);
    let cumulativeBytes = 0;

    for (let i = 0; i < files.length; i++) {
        cumulativeBytes += files[i].size;

        if (adjustedLoadedBytes < cumulativeBytes) {
            return i + 1;
        }
    }

    return files.length;
};

export const createUploadProgressHandler = (files?: File[], onUploadProgress?: UploadProgressHandler) => {
    if (!onUploadProgress) {
        return undefined;
    }

    return (event: AxiosProgressEvent) => {
        const progress = getUploadProgress(event);

        if (progress !== undefined) {
            onUploadProgress({
                progress,
                currentFileIndex:
                    typeof event.loaded === 'number' ? getCurrentFileIndex(event.loaded, files) : undefined,
                totalFiles: files?.length,
            });
        }
    };
};

export const getUploadingFileLabel = (state?: IUploadProgressState): string => {
    if (state?.currentFileIndex && state?.totalFiles) {
        return `Загружаю файл ${state.currentFileIndex} из ${state.totalFiles}...`;
    }

    return 'Загружаю файлы...';
};
